import { Button, Flex, Form, Input, InputNumber } from "antd";
import { useEffect } from "react";
import {
  useCreateCourseHighlightsMutation,
  useUpdateCoursesHighlightsMutation,
} from "../../../../app/features/admin/admin.api";
import type {
  CourseHighlight,
  CoursesHighlightsResponse,
} from "../../../../app/features/admin/admin.types";
import CustomModal from "../../../../components/CustomModal";
import type { ModalType } from "../../../../hooks/useModal";
import { toast } from "../../../../lib/toast";

interface HighlightModalProps {
  open: boolean;
  onCancel: () => void;
  initialValues?: CourseHighlight | CoursesHighlightsResponse | null;
  type?: ModalType["type"];
  meta?: { courseId?: number; type?: string; title?: string } | null;
}

const HighlightModal = ({
  open,
  onCancel,
  initialValues,
  type,
  meta,
}: HighlightModalProps) => {
  const [form] = Form.useForm();
  const [createHighlight, { isLoading: isCreating }] =
    useCreateCourseHighlightsMutation();
  const [updateHighlight, { isLoading: isUpdating }] =
    useUpdateCoursesHighlightsMutation();

  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (initialValues && type === "UPDATE") {
      form.setFieldsValue({
        text: initialValues.text,
        sortOrder: initialValues.sortOrder,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ sortOrder: 0 });
    }
  }, [initialValues, form, open, type]);

  const handleSubmit = async (values: any) => {
    try {
      const action = type === "UPDATE" ? "Update" : "Create";
      const highlightType = meta?.type ?? initialValues?.type ?? "";
      const sectionTitle = meta?.title ?? "";

      // show an info message while operation is in progress
      toast(
        "info",
        `${action} ${highlightType}${sectionTitle ? ` - ${sectionTitle}` : ""}`,
      );
      let res;
      if (initialValues?.id && type === "UPDATE") {
        res = await updateHighlight({
          id: initialValues.id,
          type: meta?.type ?? initialValues.type,
          ...values,
        }).unwrap();
      } else {
        res = await createHighlight({
          courseId: meta?.courseId,
          type: meta?.type,
          ...values,
        }).unwrap();
      }

      if (res) {
        toast(
          "success",
          `${action === "Update" ? "Updated successfully" : "Created successfully"}`,
          // `${action === "Update" ? "Updated" : "Created"} ${highlightType}${sectionTitle ? ` - ${sectionTitle}` : ""}`,
        );
        onCancel();
      }
    } catch (error: any) {
      toast("error", error?.data?.message || "Something went wrong");
    }
  };

  return (
    <CustomModal
      handleClose={onCancel}
      title={type === "UPDATE" ? "Update" : "Create"}
      open={open}
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="custom-form"
        size="large"
      >
        <Form.Item
          name="text"
          label="Text"
          rules={[{ required: true, message: "Please enter text" }]}
        >
          <Input placeholder="Enter highlight text" />
        </Form.Item>

        <Form.Item
          name="sortOrder"
          label="Sort Order"
          rules={[{ required: true }]}
        >
          <InputNumber min={0} className="w-full" />
        </Form.Item>

        <Flex justify="end" gap={8}>
          <Button disabled={isLoading} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {type === "UPDATE" ? "Update" : "Create"}
          </Button>
        </Flex>
      </Form>
    </CustomModal>
  );
};

export default HighlightModal;
